import React, { useId } from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
}

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onCheckedChange,
  disabled = false,
  className = '',
  label = 'Toggle',
}) => {
  const labelId = useId();

  return (
    <Button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-label={label}
      disabled={disabled}
      variant="ghost"
      size="sm"
      className={clsx(
        'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full',
        'border transition-colors duration-200 ease-in-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent',
        'items-center px-0.5 hover:bg-transparent',
        checked
          ? 'bg-accent border-accent'
          : 'bg-secondary border-primary shadow-sm dark:shadow-none',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && onCheckedChange(!checked)}
    >
      <span id={labelId} className="sr-only">
        {label}
      </span>
      <span
        className={clsx(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full',
          'transition-all duration-200 ease-in-out',
          'h-2 w-2 bg-[var(--text-on-accent)]',
          'border border-border/40 dark:border-border-secondary/40',
          'shadow-md',
          checked ? 'left-[calc(100%-0.5rem-0.125rem)]' : 'left-0.5'
        )}
        aria-hidden="true"
      />
    </Button>
  );
};
